import { Component, EventEmitter, HostListener, OnDestroy, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import {
  HttpRequestLog,
  NetworkActivityService,
} from '../../services/network-activity/network-activity.service';

const MIN_WIDTH = 300;
const MIN_HEIGHT = 200;

@Component({
  selector: 'frodo-network-activity-panel',
  templateUrl: './network-activity-panel.component.html',
  styleUrls: ['./network-activity-panel.component.scss'],
})
export class NetworkActivityPanelComponent implements OnDestroy {
  @Output() closed = new EventEmitter<void>();

  requests: HttpRequestLog[] = [];
  selectedRequest: HttpRequestLog | null = null;
  displayedColumns = ['method', 'url', 'status', 'duration'];
  panelWidth = 500;
  panelHeight = 400;

  private destroy$ = new Subject<void>();
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private isResizing = false;

  constructor(private networkActivityService: NetworkActivityService) {
    this.networkActivityService.requests$
      .pipe(takeUntil(this.destroy$))
      .subscribe((reqs) => (this.requests = reqs));
  }

  selectRequest(request: HttpRequestLog): void {
    this.selectedRequest = request;
  }

  clearRequests(): void {
    this.networkActivityService.clearRequests();
    this.selectedRequest = null;
  }

  closePanel(): void {
    this.closed.emit();
  }

  formatBody(body: unknown): string {
    if (body == null) return 'N/A';
    if (typeof body === 'string') return body;
    try {
      return JSON.stringify(body, null, 2);
    } catch {
      return String(body);
    }
  }

  formatHeaders(headers: Record<string, string> | undefined): string {
    if (!headers || Object.keys(headers).length === 0) return 'N/A';
    return Object.entries(headers)
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n');
  }

  isFailedRequest(req: HttpRequestLog): boolean {
    const status = req.responseStatus;
    return status == null || status === 0 || status >= 400;
  }

  startResize(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const isTouch = event instanceof TouchEvent;
    const clientX = isTouch
      ? (event as TouchEvent).touches[0].clientX
      : (event as MouseEvent).clientX;
    const clientY = isTouch
      ? (event as TouchEvent).touches[0].clientY
      : (event as MouseEvent).clientY;
    this.resizeStartX = clientX;
    this.resizeStartY = clientY;
    this.resizeStartWidth = this.panelWidth;
    this.resizeStartHeight = this.panelHeight;
    this.isResizing = true;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isResizing) return;
    const dx = event.clientX - this.resizeStartX;
    const dy = event.clientY - this.resizeStartY;
    this.panelWidth = Math.max(MIN_WIDTH, this.resizeStartWidth + dx);
    this.panelHeight = Math.max(MIN_HEIGHT, this.resizeStartHeight + dy);
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    if (this.isResizing) this.isResizing = false;
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.isResizing || !event.touches.length) return;
    event.preventDefault();
    const touch = event.touches[0];
    const dx = touch.clientX - this.resizeStartX;
    const dy = touch.clientY - this.resizeStartY;
    this.panelWidth = Math.max(MIN_WIDTH, this.resizeStartWidth + dx);
    this.panelHeight = Math.max(MIN_HEIGHT, this.resizeStartHeight + dy);
    this.resizeStartX = touch.clientX;
    this.resizeStartY = touch.clientY;
    this.resizeStartWidth = this.panelWidth;
    this.resizeStartHeight = this.panelHeight;
  }

  @HostListener('document:touchend')
  onTouchEnd(): void {
    if (this.isResizing) this.isResizing = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
